import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import { sendRequestInfo } from '../api/requestInfo';
import controlDeckLogo from '../assets/controldeckLogo.png';

const requestInfoColors = {
  ink: '#10212b',
  muted: '#5f6f7a',
  primary: '#0f766e',
  primaryDark: '#134e4a',
};

const interestOptions = [
  'CRM and customer sales statistics',
  'Margin and supply-chain control',
  'Inventory and purchase decisions',
  'Customer reports',
  'Multitenant user management',
];

const initialForm = {
  name: '',
  email: '',
  company: '',
  phone: '',
  interest: interestOptions[0],
  message: '',
};

export default function RequestInfoPage() {
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setError('');
    setSent(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextForm = {
      name: form.name.trim(),
      email: form.email.trim(),
      company: form.company.trim(),
      phone: form.phone.trim(),
      interest: form.interest.trim(),
      message: form.message.trim(),
    };

    if (!nextForm.name || !nextForm.email || !nextForm.company) {
      setError('Please enter your name, work email, and company.');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextForm.email)) {
      setError('Please enter a valid work email.');
      return;
    }

    setIsSubmitting(true);

    try {
      await sendRequestInfo(nextForm);
      setForm(initialForm);
      setSent(true);
    } catch (requestError) {
      setError(requestError.message || 'Could not send the request. Please try again later.');
      setSent(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background:
          'linear-gradient(135deg, rgba(15,118,110,0.12) 0%, rgba(238,244,248,0.96) 45%, rgba(255,255,255,1) 100%)',
        color: requestInfoColors.ink,
        overflowX: 'hidden',
      }}
    >
      <Container maxWidth="lg" disableGutters sx={{ px: { xs: 2, md: 3 }, py: { xs: 3, md: 5 } }}>
        <Stack spacing={{ xs: 3, md: 4 }}>
          <Button
            component={RouterLink}
            to="/login"
            startIcon={<ArrowBackRoundedIcon />}
            sx={{
              alignSelf: 'flex-start',
              color: requestInfoColors.primaryDark,
              fontWeight: 800,
              borderRadius: 2,
            }}
          >
            Back to ControlDeck
          </Button>

          <Box
            component="img"
            src={controlDeckLogo}
            alt="ControlDeck"
            sx={{
              width: { xs: '100%', sm: 390, md: 430 },
              maxWidth: 430,
              height: { xs: 108, sm: 112, md: 118 },
              objectFit: 'contain',
              objectPosition: 'center',
              borderRadius: 1.5,
            }}
          />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 5fr) minmax(0, 7fr)' },
              gap: 3,
              alignItems: 'start',
              minWidth: 0,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Stack spacing={2.5}>
                <Chip
                  label="Request more information"
                  sx={{
                    alignSelf: 'flex-start',
                    backgroundColor: 'rgba(15,118,110,0.1)',
                    color: requestInfoColors.primaryDark,
                    fontWeight: 800,
                  }}
                />
                <Typography
                  component="h1"
                  sx={{
                    fontSize: { xs: '2.1rem', sm: '3rem', md: '3.65rem' },
                    lineHeight: 1,
                    fontWeight: 900,
                    letterSpacing: 0,
                    overflowWrap: 'anywhere',
                  }}
                >
                  See how ControlDeck can sharpen your Fortnox operation.
                </Typography>
                <Typography variant="body1" sx={{ color: requestInfoColors.muted, lineHeight: 1.7 }}>
                  Tell us what you want to improve. We will use your details to follow up about
                  CRM, customer statistics, purchase control, reports, multitenancy, and margin
                  visibility.
                </Typography>
              </Stack>
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  p: { xs: 2, md: 3 },
                  backgroundColor: '#fff',
                  border: '1px solid rgba(16,33,43,0.08)',
                  boxShadow: '0 22px 52px rgba(16,33,43,0.1)',
                  maxWidth: '100%',
                }}
              >
                <Stack spacing={2.25}>
                  <Box>
                    <Typography variant="h5" fontWeight={900}>
                      Leave your contact details
                    </Typography>
                    <Typography variant="body2" sx={{ color: requestInfoColors.muted }}>
                      Submit the form and we will send your request directly.
                    </Typography>
                  </Box>

                  <Stack component="form" spacing={2} onSubmit={handleSubmit}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
                        gap: 1.5,
                        minWidth: 0,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <TextField
                          label="Name"
                          value={form.name}
                          onChange={updateField('name')}
                          fullWidth
                          required
                          autoComplete="name"
                          size="small"
                        />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <TextField
                          label="Work email"
                          value={form.email}
                          onChange={updateField('email')}
                          fullWidth
                          required
                          autoComplete="email"
                          size="small"
                        />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <TextField
                          label="Company"
                          value={form.company}
                          onChange={updateField('company')}
                          fullWidth
                          required
                          autoComplete="organization"
                          size="small"
                        />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <TextField
                          label="Phone"
                          value={form.phone}
                          onChange={updateField('phone')}
                          fullWidth
                          autoComplete="tel"
                          size="small"
                        />
                      </Box>
                      <Box sx={{ gridColumn: '1 / -1', minWidth: 0 }}>
                        <TextField
                          select
                          label="Main interest"
                          value={form.interest}
                          onChange={updateField('interest')}
                          fullWidth
                          size="small"
                        >
                          {interestOptions.map((option) => (
                            <MenuItem value={option} key={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>
                      <Box sx={{ gridColumn: '1 / -1', minWidth: 0 }}>
                        <TextField
                          label="Message"
                          value={form.message}
                          onChange={updateField('message')}
                          fullWidth
                          multiline
                          minRows={4}
                          placeholder="Tell us what you want to improve or which company workflows you want to connect."
                        />
                      </Box>
                    </Box>

                    {error && <Alert severity="error">{error}</Alert>}
                    {sent && (
                      <Alert severity="success">
                        Your request has been sent. We will get back to you shortly.
                      </Alert>
                    )}

                    <Button
                      variant="contained"
                      type="submit"
                      startIcon={<MailOutlineRoundedIcon />}
                      disabled={isSubmitting}
                      sx={{
                        alignSelf: { xs: 'stretch', sm: 'flex-start' },
                        backgroundColor: requestInfoColors.primary,
                        borderRadius: 2,
                        fontWeight: 850,
                        px: 3,
                        '&:hover': { backgroundColor: requestInfoColors.primaryDark },
                      }}
                    >
                      {isSubmitting ? 'Sending...' : 'Send request'}
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            </Box>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
